import { Form, Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Card } from '@hivebrite/design-system/Card';
import { Divider } from '@hivebrite/design-system/Divider';
import {
  FontAwesomeIcon,
  CATEGORIES,
} from '@hivebrite/design-system/FontAwesomeIcon';

import { useSessionInfoContext } from 'hb-react/frontoffice/contexts/SessionInfoContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import PromoCodeValidation from 'hb-react/shared/apps/Memberships/containers/PromoCode/PromoCodeValidation';
import Navigation from 'hb-react/shared/apps/Memberships/containers/PurchaseMembership/components/Navigation';
import SelectAddress from 'hb-react/shared/apps/Memberships/containers/PurchaseMembership/components/SelectAddress';
import { BILLING_TRANSLATION } from 'hb-react/shared/apps/Memberships/containers/PurchaseMembership/constants';
import { useInitialValues } from 'hb-react/shared/apps/Memberships/containers/PurchaseMembership/hooks/useInitialValues';
import { userFieldsConfiguration } from 'hb-react/shared/apps/Memberships/utils/form';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import {
  Checkbox,
  CustomAttribute,
  Email,
  Select,
  Text,
} from 'hb-react/shared/components/formik/components/inputs';
import { useShoppingCard } from 'hb-react/shared/components/ShoppingCard/hooks/useShoppingCard';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import usePersistForm from 'hb-react/shared/hooks/usePersistForm';

const ArrowLeftIcon = () => (
  <FontAwesomeIcon
    iconName="chevron-left"
    height="16px"
    iconType={CATEGORIES.SOLID}
  />
);

const BillingInformation = ({
  onSubmit,
  memberships = [],
  withPayment = true,
  onBack = () => ({}),
}) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const { memberships: membershipsSessionInfo } = useSessionInfoContext();
  const { flagEvPaymentsMovePromocode } = useFeatureFlag();

  const {
    data: {
      cart: { cart_items: cartItems },
      _metadata = {},
    },
  } = useShoppingCard();

  const { eligible_for_promocodes: eligibleForPromocodes = false } = _metadata;

  const {
    user_fields_config: userFieldsConfig,
    user_custom_attributes: userCustomAttributes = [],
  } = membershipsSessionInfo;

  const customizableAttributes = [
    ...userCustomAttributes,
    ...memberships.map(({ meta = {} }) => meta.group_customizable_attributes),
  ]
    .flat()
    .filter(Boolean);

  const initialValues = useInitialValues({
    currentUser,
    customizable_attributes: customizableAttributes,
  });

  const isShippable = cartItems?.some((item) => item.is_shippable);

  const submitHandler = async (values, { setSubmitting }) => {
    setSubmitting(true);

    await onSubmit(values);
    setSubmitting(false);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={submitHandler}>
      {({ submitForm, isValid, isSubmitting }) => (
        <FormWrapper>
          <ErrorFocus />
          <PersonalInformation
            customizableAttributes={customizableAttributes}
            userFieldsConfig={userFieldsConfig}
            currentNetwork={currentNetwork}
          />
          <BillingAddress
            currentUser={currentUser}
            currentNetwork={currentNetwork}
            isShippable={isShippable}
          />
          {!flagEvPaymentsMovePromocode && eligibleForPromocodes && (
            <PromoCode />
          )}
          <NavBar
            onBack={onBack}
            isValid={isValid}
            isLoading={isSubmitting}
            submitForm={submitForm}
            withPayment={withPayment}
          />
        </FormWrapper>
      )}
    </Formik>
  );
};

const PersonalInformation = ({
  userFieldsConfig,
  customizableAttributes = [],
}) => {
  const { getLabel } = userFieldsConfiguration(userFieldsConfig);

  return (
    <Card bg="white" mb={5} pt={4} pb={8}>
      <CardHeader>{s__('Memberships|Personal information')}</CardHeader>

      <FormGrid>
        <>
          <Text
            name="firstname"
            label={getLabel(
              'firstname_label',
              'activerecord.attributes.user.firstname',
            )}
            placeholder={getLabel(
              'firstname_placeholder',
              'activerecord.attributes.user.firstname',
            )}
            validate={{ required: true }}
          />
          <Text
            name="lastname"
            label={getLabel(
              'lastname_label',
              'activerecord.attributes.user.lastname',
            )}
            placeholder={getLabel(
              'lastname_placeholder',
              'activerecord.attributes.user.lastname',
            )}
            validate={{ required: true }}
          />
          <Email
            name="email"
            label={getLabel(
              'email_label',
              'activerecord.attributes.user.email',
            )}
            placeholder={getLabel(
              'email_placeholder',
              'activerecord.attributes.user.email',
            )}
            validate={{ required: true, email: true }}
          />

          {customizableAttributes.map((attribute) => (
            <FormControl
              fullWidth={attribute.attribute_type === 'double_select'}
              key={attribute.id}
            >
              <CustomAttribute
                name={attribute.name}
                customAttribute={attribute}
                labelMultiline
              />
            </FormControl>
          ))}
        </>
      </FormGrid>
    </Card>
  );
};

const BillingAddress = ({ currentUser, currentNetwork, isShippable }) => {
  const { setFieldValue, values } = useFormikContext();
  const { getPostalPlaceholder } = userFieldsConfiguration(
    currentNetwork.user_fields_config,
  );

  const getPlaceholderByType = getPostalPlaceholder(values.address_type);

  return (
    <Card bg="white" mb={5} pt={4} pl={6} pb={8}>
      <CardHeader>{s__('Memberships|Billing address')}</CardHeader>

      <FormGrid>
        <FormControl fullWidth>
          <SelectAddress
            name="postal_address_type"
            currentUser={currentUser}
            setSelectedAddress={(addressSchema) =>
              setFieldValue('billing_address_attributes', addressSchema)
            }
          />
        </FormControl>
        <Text
          name="billing_address_attributes[address_1]"
          label={I18n.t(BILLING_TRANSLATION.ADDRESS_1)}
          placeholder={
            getPlaceholderByType('address_1') ||
            I18n.t(BILLING_TRANSLATION.ADDRESS_1)
          }
          validate={{ required: true }}
        />
        <Text
          name="billing_address_attributes[address_2]"
          label={I18n.t(BILLING_TRANSLATION.ADDRESS_2)}
          placeholder={
            getPlaceholderByType('address_2') ||
            I18n.t(BILLING_TRANSLATION.ADDRESS_2)
          }
        />
        <Text
          name="billing_address_attributes[address_3]"
          label={I18n.t(BILLING_TRANSLATION.ADDRESS_3)}
          placeholder={
            getPlaceholderByType('address_3') ||
            I18n.t(BILLING_TRANSLATION.ADDRESS_3)
          }
        />

        <Text
          name="billing_address_attributes[city]"
          placeholder={I18n.t(BILLING_TRANSLATION.CITY)}
          label={I18n.t(BILLING_TRANSLATION.CITY)}
          validate={{ required: true }}
        />
        <Text
          name="billing_address_attributes[state]"
          placeholder={I18n.t(BILLING_TRANSLATION.STATE)}
          label={I18n.t(BILLING_TRANSLATION.STATE)}
        />

        <Text
          name="billing_address_attributes[postal_code]"
          placeholder={I18n.t(BILLING_TRANSLATION.POSTAL_CODE)}
          label={I18n.t(BILLING_TRANSLATION.POSTAL_CODE)}
          validate={{ required: true }}
        />

        <Select
          type="countries"
          name="billing_address_attributes[country]"
          placeholder={I18n.t(BILLING_TRANSLATION.COUNTRY)}
          label={I18n.t(BILLING_TRANSLATION.COUNTRY)}
          inputHtml={{
            maxMenuHeight: 160,
          }}
          validate={{ required: true }}
        />
      </FormGrid>
      {isShippable && (
        <ShippingAddress getPlaceholderByType={getPlaceholderByType} />
      )}
    </Card>
  );
};

const CardHeader = ({ children }) => (
  <>
    <Box font="h3-semibold" mb={3}>
      {children}
    </Box>
    <Divider borderBottomColor="horizon" mt={0} mb={5} width="100%" />
  </>
);

const NavBar = ({
  onBack,
  submitForm,
  isLoading,
  isValid = false,
  withPayment = true,
}) => (
  <Navigation>
    {({ isFixed }) => (
      <Box
        flexDirection={isFixed ? 'column' : 'row'}
        width="100%"
        justifyContent="space-between"
      >
        <Box
          alignSelf="flex-end"
          justifyContent="space-between"
          width={isFixed ? 'auto' : '100%'}
        >
          <Button
            variant="secondary"
            mr={2}
            icon={!isFixed && ArrowLeftIcon}
            onClick={() => onBack()}
          >
            {s__('Memberships|Go back to registration')}
          </Button>

          <Button
            isDisabled={!isValid}
            isLoading={isLoading}
            onClick={submitForm}
          >
            {withPayment ? s__('Memberships|Proceed to Payment') : __('Submit')}
          </Button>
        </Box>
      </Box>
    )}
  </Navigation>
);

const ShippingAddress = ({ getPlaceholderByType }) => {
  const { values } = useFormikContext();

  return (
    <FormGrid>
      <FormControl fullWidth>
        <Checkbox
          name="shipping_address_attributes[same_as_billing]"
          label={s__('Memberships|Billing address is same as shipping address')}
          formicRefacto
        />
      </FormControl>
      {!values.shipping_address_attributes?.same_as_billing && (
        <>
          <FormControl fullWidth>
            <Box my={5} font="h4-semibold">
              {s__('Memberships|Enter shipping address details')}:
            </Box>

            <Text
              name="shipping_address_attributes[full_name]"
              label={s__('Memberships|Full name')}
              placeholder={s__('Memberships|Full name')}
              validate={{ required: true }}
              wrapperProps={{
                width: '48%',
              }}
            />
          </FormControl>
          <Text
            name="shipping_address_attributes[address_1]"
            label={I18n.t(BILLING_TRANSLATION.ADDRESS_1)}
            placeholder={
              getPlaceholderByType('address_1') ||
              I18n.t(BILLING_TRANSLATION.ADDRESS_1)
            }
            validate={{ required: true }}
          />

          <Text
            name="shipping_address_attributes[address_2]"
            label={I18n.t(BILLING_TRANSLATION.ADDRESS_2)}
            placeholder={
              getPlaceholderByType('address_2') ||
              I18n.t(BILLING_TRANSLATION.ADDRESS_2)
            }
          />

          <Text
            name="shipping_address_attributes[address_3]"
            label={I18n.t(BILLING_TRANSLATION.ADDRESS_3)}
            placeholder={
              getPlaceholderByType('address_3') ||
              I18n.t(BILLING_TRANSLATION.ADDRESS_3)
            }
          />

          <Text
            name="shipping_address_attributes[city]"
            placeholder={s__('Memberships|City')}
            label={s__('Memberships|City')}
            validate={{ required: true }}
          />

          <Text
            name="shipping_address_attributes[state]"
            placeholder={s__('Memberships|State')}
            label={s__('Memberships|State')}
          />

          <Text
            name="shipping_address_attributes[postal_code]"
            placeholder={s__('Memberships|Postal code')}
            label={s__('Memberships|Postal code')}
            validate={{ required: true }}
          />

          <Select
            type="countries"
            name="shipping_address_attributes[country]"
            placeholder={I18n.t(BILLING_TRANSLATION.COUNTRY)}
            label={I18n.t(BILLING_TRANSLATION.COUNTRY)}
            inputHtml={{
              maxMenuHeight: 160,
            }}
            validate={{ required: true }}
          />
        </>
      )}
    </FormGrid>
  );
};

const PromoCode = () => (
  <Card bg="white" mb={5} pt={4} pl={6} pb={8}>
    <CardHeader>{s__('Memberships|Promo Code')}</CardHeader>
    <PromoCodeValidation />
  </Card>
);

const FormWrapper = ({ children }) => {
  const {
    data: { cart },
  } = useShoppingCard();

  usePersistForm(`billing_form_${cart.id}`);

  return <StyledForm>{children}</StyledForm>;
};

BillingInformation.propTypes = {
  membership: PropTypes.shape({
    group_customizable_attributes: PropTypes.array,
  }),
};

export default BillingInformation;

const FormGrid = styled(Box)`
  max-width: 536px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
`;

const FormControl = styled(Box).withConfig({
  shouldForwardProp: (prop) => !['fullWidth'].includes(prop),
})`
  ${({ fullWidth }) => fullWidth && { gridColumn: '1 / 2 span' }}
  display: block;
  width: 100%;
`;

const StyledForm = styled(Form)`
  width: 100%;
`;
